
import { ReactNode, useEffect, useRef } from "react";
import { User } from "@/types/auth";
import { supabase } from "@/integrations/supabase/client";
import { setCachedUser, clearCachedUserId } from "@/services/auth/sessionCache";
import { AuthChangeEvent } from "@supabase/supabase-js";
import logger from "@/utils/logger";
import { syncService } from "@/services/sync.service";
import { clearAllTables } from "@/db/offlineDb";
import { syncValidationService } from "@/services/sync/SyncValidationService";
import { networkService } from "@/services/network.service";
import { getValidUserId } from '@/utils/validation';
import { safeToastError } from "@/components/ui/toastManager";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { logoutCleanupService } from "@/services/auth/logoutCleanup.service";
import { postLoginCoordinator } from "@/services/auth/postLoginCoordinator";

const hasSupabaseConfig = Boolean((import.meta as any).env?.VITE_SUPABASE_URL) &&
  Boolean((import.meta as any).env?.VITE_SUPABASE_ANON_KEY);

// Permette a componenti esterni di attendere la conclusione
// delle operazioni di cleanup successive al logout.
const signOutResolvers: Array<() => void> = [];
export const waitForSignOut = () =>
  new Promise<void>((resolve) => {
    signOutResolvers.push(resolve);
  });

interface AuthStateListenerProps {
  children: ReactNode;
  onUserChange: (user: User | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  fetchCurrentUser: () => Promise<User | null>;
}

export const AuthStateListener = ({ children, onUserChange, setIsLoading, fetchCurrentUser }: AuthStateListenerProps) => {
  const subscription = useRef<{ data: { subscription: any } } | null>(null);
  const { setThemeId, refreshTheme } = useThemeConfig();

  const onUserChangeRef = useRef(onUserChange);
  const setIsLoadingRef = useRef(setIsLoading);
  const fetchCurrentUserRef = useRef(fetchCurrentUser);
  const setThemeIdRef = useRef(setThemeId);
  const refreshThemeRef = useRef(refreshTheme);

  // Track timeout IDs for dispatching events so they can be cleared
  const timeoutIds = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const setManagedTimeout = (
    key: string,
    fn: () => void | Promise<void>,
    delay: number
  ) => {
    const existing = timeoutIds.current[key];
    if (existing) {
      clearTimeout(existing);
    }
    timeoutIds.current[key] = window.setTimeout(() => {
      void fn();
      delete timeoutIds.current[key];
    }, delay);
  };

  const clearAllTimeouts = () => {
    Object.values(timeoutIds.current).forEach((id) => clearTimeout(id));
    timeoutIds.current = {};
  };

  useEffect(() => {
    onUserChangeRef.current = onUserChange;
    setIsLoadingRef.current = setIsLoading;
    fetchCurrentUserRef.current = fetchCurrentUser;
    setThemeIdRef.current = setThemeId;
    refreshThemeRef.current = refreshTheme;
  });

  useEffect(() => {
    logger.log("[AUTH_LISTENER] Setting up auth listener");
    
    // ✅ NUOVO: Disabilita il sync periodico per evitare conflitti
    syncService.disablePeriodicSync();
    
    const setupAuthListener = async () => {
      try {
        setIsLoadingRef.current(true);
        
        // Set up auth state listener
        subscription.current = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session) => {
          logger.log("[AUTH_LISTENER] 🔥 Auth state change event:", event, "Session exists:", !!session);
          
          if (event === 'PASSWORD_RECOVERY') {
            logger.log("[AUTH_LISTENER] 🔐 PASSWORD_RECOVERY detected - awaiting explicit login");
            onUserChangeRef.current(null);
            return;
          } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            if (session) {
              if (event === 'SIGNED_IN' && !postLoginCoordinator.isPostLogin()) {
                postLoginCoordinator.start();
              }
              const previousId = getValidUserId();
              if (session.user?.id) {
                setCachedUser(session.user);
                localStorage.setItem('userId', session.user.id);
              }

              // Use managed timeout to avoid Supabase deadlock
              setManagedTimeout('authEvent', async () => {
                try {
                  let user = await fetchCurrentUserRef.current();
                  if (!user) {
                    logger.warn('[AUTH_LISTENER] fetchCurrentUser returned null - retrying');
                    await new Promise((resolve) => setTimeout(resolve, 250));
                    user = await fetchCurrentUserRef.current();
                  }
                  onUserChangeRef.current(user);

                  if (user?.id) {
                    if (previousId && previousId !== user.id) {
                      await clearAllTables();
                      logger.log('[AUTH_LISTENER] 📤 Invoking performComprehensiveValidation', {
                        userId: previousId.substring(0, 8),
                        stack: new Error().stack
                      });
                      await syncValidationService.performComprehensiveValidation(
                        previousId,
                        true
                      );
                    }
                  }

                  await refreshThemeRef.current();

                  // ✅ NUOVO: Eventi più specifici e con logging dettagliato
                  if (event === 'SIGNED_IN' && user?.id) {
                    logger.log("[AUTH_LISTENER] 🔥🔥🔥 SIGNED_IN detected - Emitting userSignedIn event", {
                      userId: user.id.substring(0, 8),
                      event,
                      timestamp: Date.now()
                    });

                    setManagedTimeout('userSignedIn', () => {
                      const eventDetail = {
                        userId: user.id,
                        timestamp: Date.now(),
                        event: 'SIGNED_IN'
                      };

                      logger.log("[AUTH_LISTENER] 📢 Dispatching userSignedIn event", eventDetail);
                      window.dispatchEvent(new CustomEvent('userSignedIn', {
                        detail: eventDetail
                      }));
                    }, 500);
                  }
                } catch (error) {
                  logger.logError("AuthStateListener","Error fetching user after auth event",{additionalInfo:error});
                  onUserChangeRef.current(null);
                } finally {
                  setIsLoadingRef.current(false);
                }
              }, 150);
            }
          } else if (event === 'SIGNED_OUT') {
            logger.log("[AUTH_LISTENER] 🚪 SIGNED_OUT detected");

            setManagedTimeout('signOutCleanup', async () => {
              try {
                clearAllTimeouts();
                onUserChangeRef.current(null);
                postLoginCoordinator.end();
                await logoutCleanupService.performCleanup(
                  setThemeIdRef.current,
                  refreshThemeRef.current
                );
              } catch (error) {
                logger.logError(
                  "AuthStateListener",
                  "Error during sign out cleanup",
                  { additionalInfo: error }
                );
              } finally {
                // Notifica eventuali listener che il cleanup è concluso
                signOutResolvers.forEach((resolve) => resolve());
                signOutResolvers.length = 0;

                // ✅ Cleanup al logout
                setManagedTimeout('userSignedOut', () => {
                  window.dispatchEvent(
                    new CustomEvent('userSignedOut', {
                      detail: { timestamp: Date.now() }
                    })
                  );
                }, 0);

                setIsLoadingRef.current(false);
              }
            }, 0);
          }
        });
        logger.log("[AUTH_LISTENER] Subscribed to auth state changes");

        if (!networkService.isOnline()) {
          logger.warn('[AUTH_LISTENER] Offline mode - skipping initial session check');
          onUserChangeRef.current(null);
          setIsLoadingRef.current(false);
          return;
        }

        // Initial session check
        const { data } = await supabase.auth.getSession();
        const initialStatus = data.session ? "Attiva" : "Non attiva";
        logger.log("[AUTH_LISTENER] 🔍 Sessione iniziale:", initialStatus);

        if (data.session) {
          setCachedUser(data.session.user);
          localStorage.setItem('userId', data.session.user.id);
          try {
            const user = await fetchCurrentUserRef.current();
            if (!user) {
              logger.warn('[AUTH_LISTENER] fetchCurrentUser returned null during initial session check');
            }
            onUserChangeRef.current(user);

            // ✅ NUOVO: Trigger sync anche per sessioni esistenti con logging dettagliato
            if (user?.id) {
              logger.log("[AUTH_LISTENER] 🔄🔄🔄 Session restored - Emitting userSessionRestored event", {
                userId: user.id.substring(0, 8),
                event: 'SESSION_RESTORED',
                timestamp: Date.now()
              });

              setManagedTimeout('userSessionRestored', () => {
              const eventDetail = {
                userId: user.id,
                timestamp: Date.now(),
                event: 'SESSION_RESTORED'
              };

                logger.log("[AUTH_LISTENER] 📢 Dispatching userSessionRestored event", eventDetail);
              window.dispatchEvent(new CustomEvent('userSessionRestored', {
                detail: eventDetail
              }));
            }, 2000);
            }
          } catch (error) {
            logger.logError("AuthStateListener","Error fetching initial user",{additionalInfo:error});
            onUserChangeRef.current(null);
          }
        } else {
          logger.log("[AUTH_LISTENER] No initial session found");
          const lostUserId = getValidUserId();
          await clearAllTables();
          if (lostUserId) {
            logger.log('[AUTH_LISTENER] 📤 Invoking performComprehensiveValidation', {
              userId: lostUserId.substring(0, 8),
              stack: new Error().stack
            });
            await syncValidationService.performComprehensiveValidation(lostUserId, true);
          }
          onUserChangeRef.current(null);
          clearCachedUserId();
        }
      } catch (error) {
        const online = networkService.isOnline();
        logger.logError(
          "AuthStateListener",
          "Setup error",
          {
            errorCode: "AUTH_LISTENER_SETUP",
            additionalInfo: { error, online, hasSupabaseConfig }
          }
        );

        if (!online) {
          safeToastError("Impossibile configurare l'autenticazione", {
            description: 'Connessione assente'
          });
        } else if (!hasSupabaseConfig) {
          safeToastError("Configurazione di autenticazione mancante", {
            description: 'Controlla le variabili di ambiente'
          });
        } else {
          safeToastError("Impossibile configurare l'autenticazione");
        }

        onUserChangeRef.current(null);
      } finally {
        setIsLoadingRef.current(false);
      }
    };
    
    setupAuthListener();
    
    // Clean up function
    return () => {
      logger.log("[AUTH_LISTENER] Rimozione listener di autenticazione");
      clearAllTimeouts();
      subscription.current?.data?.subscription?.unsubscribe();
      logger.log("[AUTH_LISTENER] Unsubscribed from auth state changes");
      subscription.current = null;
    };
  }, []);
  
  return <>{children}</>;
};
